import { Languages } from "lucide-react";
import type { MoneyPageContent } from "./types";

/**
 * SEO for the US Hispanic / bilingual market — the site's core differentiator.
 * Scope: reaching the US Hispanic audience through search (English + Spanish).
 * Not to be confused with international SEO (multi-country expansion).
 */
export const hispanicSeoPage: MoneyPageContent = {
  id: "hispanic-seo",
  icon: Languages,
  serviceType: "Hispanic & Bilingual SEO",
  esPath: "/servicios/seo-bilingue-mercado-hispano",
  enPath: "/en/services/hispanic-market-seo",
  es: {
    metaTitle: "SEO Bilingüe para el Mercado Hispano de EE. UU.",
    metaDescription:
      "SEO para llegar al consumidor hispano en Estados Unidos: comportamiento de búsqueda bilingüe, arquitectura EN/ES y estrategia por región.",
    keywords: [
      "seo mercado hispano",
      "seo bilingüe estados unidos",
      "seo para hispanos en usa",
      "marketing digital hispano",
      "seo en español para empresas en estados unidos",
    ],
    h1: "SEO para conectar marcas con el mercado hispano de Estados Unidos",
    breadcrumbLabel: "SEO Mercado Hispano",
    heroSubtitle: "El mercado hispano de EE. UU. no se alcanza traduciendo palabras clave en inglés",
    heroDescription:
      "Una misma persona puede buscar en inglés por la mañana y en español por la noche, según el tema, el dispositivo o con quién esté hablando. Diseño estrategias de contenido y arquitectura de sitio pensadas para ese comportamiento bilingüe real, no para una traducción genérica del sitio en inglés.",
    ctaPrimary: {
      label: "Explorar oportunidades en el mercado hispano",
      to: "/diagnostico-seo-gratis",
    },
    ctaSecondary: { label: "Ver casos documentados", to: "/casos-de-exito" },
    summary: [
      {
        label: "Comportamiento bilingüe",
        text: "La misma audiencia busca en inglés y en español según el contexto, no según un perfil demográfico fijo.",
      },
      {
        label: "Variación regional",
        text: "El español de un usuario en Miami, Houston o Los Ángeles no es intercambiable; el país de origen y la región influyen en el vocabulario de búsqueda.",
      },
      {
        label: "Arquitectura, no solo traducción",
        text: "Un sitio bilingüe mal estructurado (hreflang, canonicals, contenido duplicado) puede perjudicar a ambas versiones en Google.",
      },
      {
        label: "Competencia distinta",
        text: "Los resultados en español para una consulta suelen tener menos competidores fuertes que los mismos resultados en inglés.",
      },
    ],
    explanation: {
      title: "Qué es el SEO para el mercado hispano de EE. UU.",
      definition:
        "Es la práctica de investigar, estructurar y posicionar contenido para llegar a usuarios hispanos que viven en Estados Unidos, considerando que buscan en inglés, en español o alternando entre ambos según el contexto, el tema y el momento. No es traducir el sitio en inglés palabra por palabra ni asumir que 'el mercado hispano' busca de una sola forma.",
      whyImportant: {
        title: "Por qué importa tratarlo como una disciplina propia",
        text: "Muchas marcas asumen que basta con traducir su sitio en inglés al español, o que el inglés ya 'cubre' a toda su audiencia. Ambos supuestos dejan tráfico y clientes sobre la mesa: hay consultas donde el usuario prefiere buscar en español aunque domine el inglés (temas familiares, de salud, financieros o de confianza), y hay consultas donde el término en inglés se usa incluso dentro de una búsqueda mayormente en español (préstamo léxico o 'Spanglish' en ciertos sectores). Ignorar esa mezcla significa optimizar solo para una fracción de la demanda real.",
      },
      consequences: {
        title: "Qué se pierde al no adaptar la estrategia",
        text: "Páginas en español que son traducciones literales del inglés, sin adaptar el vocabulario de búsqueda real; contenido duplicado o señales de hreflang mal configuradas que confunden a Google sobre qué versión mostrar en cada país o idioma; campañas dirigidas a 'el mercado hispano' como bloque único, sin distinguir diferencias por región o país de origen; y una versión en español tratada como secundaria, con menos profundidad de contenido que la versión en inglés, lo que la debilita frente a competidores que sí invierten en ella.",
      },
    },
    problems: {
      title: "Errores comunes al abordar el mercado hispano",
      subtitle: "La mayoría no son de traducción, sino de estrategia de búsqueda",
      items: [
        {
          icon: Languages,
          title: "Traducir en vez de localizar palabras clave",
          description:
            "Se traduce el keyword research en inglés al español, sin verificar cómo busca realmente el usuario hispano ese mismo tema o producto.",
          impact: "Contenido en español que no coincide con el volumen ni la intención real de búsqueda",
        },
        {
          icon: Languages,
          title: "Tratar 'lo hispano' como un bloque homogéneo",
          description:
            "Se ignora que el vocabulario, las referencias culturales y las prioridades varían según región de EE. UU. y país de origen del usuario.",
          impact: "Contenido genérico que no conecta con audiencias específicas",
        },
        {
          icon: Languages,
          title: "Arquitectura EN/ES mal configurada",
          description:
            "Hreflang ausente o mal implementado, canonicals cruzados entre idiomas o páginas en español creadas como subcarpetas huérfanas sin enlazado interno.",
          impact: "Google indexa la versión equivocada o ignora una de las dos por completo",
        },
        {
          icon: Languages,
          title: "Versión en español tratada como secundaria",
          description:
            "Menos contenido, menos actualizaciones y menos inversión en la versión en español que en la versión en inglés del mismo sitio.",
          impact: "La versión en español pierde frente a competidores en español mejor cuidados",
        },
        {
          icon: Languages,
          title: "No considerar el recorrido bilingüe del usuario",
          description:
            "El mismo usuario puede investigar en español y decidir o comprar en un sitio en inglés, o al revés; no medir ese cruce oculta el verdadero origen de las conversiones.",
          impact: "Atribución incorrecta y decisiones basadas en datos incompletos",
        },
      ],
    },
    methodology: {
      title: "Cómo se aborda la estrategia",
      subtitle: "Investigación de audiencia primero, arquitectura técnica después, contenido al final",
      steps: [
        {
          number: "01",
          title: "Comportamiento de búsqueda inglés vs. español",
          description:
            "Análisis de qué temas de tu categoría se buscan predominantemente en inglés, cuáles en español y cuáles en ambos, y en qué momento del recorrido del usuario ocurre cada uno.",
        },
        {
          number: "02",
          title: "Localización real de palabras clave",
          description:
            "Investigación de cómo formula la búsqueda el usuario hispano en EE. UU. —no una traducción del inglés— incluyendo términos prestados del inglés cuando así se usan realmente en la categoría.",
        },
        {
          number: "03",
          title: "Investigación de mercado hispano por región",
          description:
            "Identificación de variaciones relevantes según zona geográfica y país de origen predominante, sin recurrir a generalizaciones ni cifras no verificadas.",
        },
        {
          number: "04",
          title: "Arquitectura bilingüe del sitio",
          description:
            "Definición de estructura EN/ES (subcarpetas o subdominios), implementación correcta de hreflang y canonicals, y revisión de que ninguna versión quede como contenido delgado o duplicado.",
        },
        {
          number: "05",
          title: "Estrategia de contenido diferenciada",
          description:
            "Contenido en español creado para su propia intención de búsqueda, no adaptado del inglés como segunda prioridad, con la misma profundidad editorial en ambos idiomas.",
        },
        {
          number: "06",
          title: "Búsqueda local y comportamiento de compra",
          description:
            "Optimización de presencia local (Google Business Profile, directorios, señales de proximidad) considerando cómo busca negocios el usuario hispano en su idioma preferido.",
        },
        {
          number: "07",
          title: "Medición del cruce bilingüe",
          description:
            "Configuración de analítica que permita ver cuándo un usuario investiga en un idioma y convierte en otro, para no subestimar el aporte real de la versión en español.",
        },
      ],
      ctaText: "Solicitar diagnóstico estratégico",
      ctaLink: "/diagnostico-seo-gratis",
    },
    scope: {
      includes: {
        title: "Qué incluye este servicio",
        items: [
          "Investigación de comportamiento de búsqueda bilingüe (inglés/español) por categoría",
          "Localización de palabras clave, no traducción literal",
          "Auditoría y corrección de arquitectura EN/ES (hreflang, canonicals, estructura de URLs)",
          "Estrategia de contenido diferenciada por idioma e intención",
          "Optimización de búsqueda local para audiencia hispana",
          "Consideraciones de comercio electrónico bilingüe cuando aplica",
          "Medición del recorrido de usuario a través de ambos idiomas",
        ],
      },
      notIncludes: {
        title: "Qué no incluye",
        items: [
          "Expansión a otros países o mercados internacionales (ver SEO internacional)",
          "Traducción de contenido como servicio aislado sin estrategia de búsqueda",
          "Gestión de campañas de publicidad paga (Google Ads, Meta Ads)",
          "Producción de contenido en video o diseño gráfico",
          "Traducción certificada o legal de documentos",
        ],
      },
    },
    forWho: {
      idealFor: {
        title: "Para quién tiene sentido",
        items: [
          "Empresas en EE. UU. que ya sirven o quieren servir a clientes hispanos",
          "Marcas con sitio en inglés que están considerando lanzar una versión en español",
          "Negocios con presencia en español que sospechan que no está generando resultados",
          "Empresas en sectores donde la decisión de compra suele tomarse en el idioma de preferencia del usuario (salud, servicios financieros, legal, hogar)",
          "Negocios locales o regionales con concentración de clientes hispanos",
        ],
      },
      notFor: {
        title: "Para quién no es el siguiente paso",
        items: [
          "Empresas que buscan expandirse a México, España o Latinoamérica como mercados propios (eso es SEO internacional)",
          "Negocios sin capacidad de mantener contenido y atención al cliente en ambos idiomas",
          "Marcas que buscan una traducción rápida sin compromiso con una estrategia bilingüe sostenida",
        ],
      },
    },
    faq: {
      title: "Preguntas frecuentes",
      items: [
        {
          question: "¿Basta con traducir mi sitio web al español?",
          answer:
            "No. Una traducción resuelve el idioma, pero no la intención de búsqueda ni la arquitectura técnica. Sin localizar las palabras clave y sin configurar correctamente hreflang y canonicals, la versión en español puede terminar compitiendo mal, o incluso perjudicando, a la versión en inglés.",
        },
        {
          question: "¿El mercado hispano busca todo en español?",
          answer:
            "No necesariamente. Muchos usuarios bilingües alternan entre inglés y español según el tema, el dispositivo o el contexto. Por eso la investigación de comportamiento de búsqueda es el primer paso, no una suposición.",
        },
        {
          question: "¿Esto es lo mismo que SEO internacional?",
          answer:
            "No. El SEO para el mercado hispano de EE. UU. se enfoca en usuarios que viven en Estados Unidos y buscan en inglés y/o español. El SEO internacional se enfoca en expandirse a otros países como México, España o el resto de Latinoamérica. Son estrategias distintas, aunque pueden compartir parte de la investigación de idioma.",
        },
        {
          question: "¿Necesito contenido idéntico en ambos idiomas?",
          answer:
            "No siempre, y de hecho forzarlo suele ser un error. Cada idioma puede necesitar su propio ángulo de contenido según cómo busca ese segmento de audiencia, aunque el tema de fondo sea el mismo.",
        },
        {
          question: "¿Cómo sé si mi versión en español ya está funcionando o no?",
          answer:
            "Se revisa mediante Search Console segmentado por idioma/país, comportamiento en analítica y una auditoría de cómo está indexada y enlazada esa versión frente a la versión en inglés.",
        },
      ],
    },
    related: {
      title: "Servicios relacionados",
      subtitle: "Para complementar la estrategia bilingüe",
      links: [
        {
          label: "SEO internacional",
          description: "Si el objetivo es expandirse a otros países, no solo llegar al mercado hispano de EE. UU.",
          to: "/servicios/seo-internacional",
        },
        {
          label: "SEO local",
          description: "Presencia en búsquedas locales para negocios con clientela hispana en su zona.",
          to: "/seo-local",
        },
        {
          label: "SEO para ecommerce",
          description: "Arquitectura y contenido de tienda online adaptados a comprador bilingüe.",
          to: "/servicios/ecommerce-seo",
        },
        {
          label: "SEO técnico",
          description: "Base técnica necesaria para que una arquitectura EN/ES funcione correctamente.",
          to: "/servicios/seo-tecnico",
        },
        {
          label: "Consultoría SEO",
          description: "Acompañamiento estratégico continuo más allá de un proyecto puntual.",
          to: "/consultor-seo",
        },
      ],
    },
    llmSummary:
      "Servicio de SEO orientado a alcanzar al mercado hispano de Estados Unidos considerando comportamiento de búsqueda bilingüe (inglés/español), localización real de palabras clave y arquitectura de sitio EN/ES correctamente implementada.",
    keyPoints: [
      "El mismo usuario bilingüe puede buscar en inglés o español según el contexto",
      "La localización de palabras clave no es traducción literal",
      "La arquitectura EN/ES requiere hreflang y canonicals correctos",
      "Hay variación relevante por región y país de origen dentro del mercado hispano",
      "Distinto del SEO internacional, que se enfoca en expansión a otros países",
    ],
  },
  en: {
    metaTitle: "Hispanic Market SEO for US Businesses",
    metaDescription:
      "SEO strategy to reach US Hispanic consumers: bilingual search behavior, EN/ES site architecture and region-aware content.",
    keywords: [
      "hispanic market seo",
      "bilingual seo services",
      "seo for hispanic consumers",
      "us hispanic digital marketing",
      "spanish seo for us companies",
    ],
    h1: "SEO to Connect Brands with the US Hispanic Market",
    breadcrumbLabel: "Hispanic Market SEO",
    heroSubtitle: "You can't reach the US Hispanic market by translating your English keywords",
    heroDescription:
      "The same person might search in English in the morning and in Spanish at night, depending on the topic, the device, or who they're talking to. I build content strategy and site architecture around that real bilingual behavior, not a generic translation of the English site.",
    ctaPrimary: {
      label: "Explore Hispanic market opportunities",
      to: "/en/free-seo-diagnosis",
    },
    ctaSecondary: { label: "See documented work", to: "/en/case-studies" },
    summary: [
      {
        label: "Bilingual behavior",
        text: "The same audience searches in English and Spanish depending on context, not on a fixed demographic profile.",
      },
      {
        label: "Regional variation",
        text: "Spanish spoken by a user in Miami, Houston, or Los Angeles isn't interchangeable; country of origin and region shape search vocabulary.",
      },
      {
        label: "Architecture, not just translation",
        text: "A poorly structured bilingual site (hreflang, canonicals, duplicate content) can hurt both language versions in Google.",
      },
      {
        label: "Different competitive landscape",
        text: "Spanish-language results for a given query often have fewer strong competitors than the same results in English.",
      },
    ],
    explanation: {
      title: "What Hispanic market SEO means",
      definition:
        "It's the practice of researching, structuring, and optimizing content to reach Hispanic users living in the United States, accounting for the fact that they search in English, in Spanish, or switch between both depending on context, topic, and moment. It is not translating the English site word for word, nor assuming 'the Hispanic market' searches one single way.",
      whyImportant: {
        title: "Why it needs to be treated as its own discipline",
        text: "Many brands assume translating their English site to Spanish is enough, or that English already 'covers' their whole audience. Both assumptions leave traffic and customers on the table: some queries are searched in Spanish even by fully bilingual users (family, health, financial, or trust-related topics), and some Spanish-dominant searches still include borrowed English terms in certain categories. Ignoring that mix means optimizing for only a fraction of real demand.",
      },
      consequences: {
        title: "What gets lost without an adapted strategy",
        text: "Spanish pages that are literal translations of English content, without localized search vocabulary; duplicate content or misconfigured hreflang signals that confuse Google about which version to show, in which country or language; campaigns targeting 'the Hispanic market' as a single block, with no distinction by region or country of origin; and a Spanish version treated as an afterthought, with less content depth than the English version — which weakens it against competitors who do invest in it.",
      },
    },
    problems: {
      title: "Common mistakes when approaching the Hispanic market",
      subtitle: "Most aren't translation problems — they're search strategy problems",
      items: [
        {
          icon: Languages,
          title: "Translating keywords instead of localizing them",
          description:
            "English keyword research gets translated to Spanish without verifying how Hispanic users actually search for that topic or product.",
          impact: "Spanish content that doesn't match real search volume or intent",
        },
        {
          icon: Languages,
          title: "Treating 'Hispanic' as one homogeneous group",
          description:
            "Vocabulary, cultural references, and priorities vary by US region and by the user's country of origin — treating it as one segment flattens that out.",
          impact: "Generic content that doesn't connect with specific audiences",
        },
        {
          icon: Languages,
          title: "Misconfigured EN/ES architecture",
          description:
            "Missing or broken hreflang, cross-language canonicals, or Spanish pages built as orphaned subfolders with no internal linking.",
          impact: "Google indexes the wrong version or ignores one entirely",
        },
        {
          icon: Languages,
          title: "Spanish version treated as secondary",
          description:
            "Less content, fewer updates, and less investment in the Spanish version than in the English version of the same site.",
          impact: "The Spanish version loses to better-maintained Spanish-language competitors",
        },
        {
          icon: Languages,
          title: "Ignoring the bilingual user journey",
          description:
            "The same user might research in Spanish and buy on an English site, or vice versa; not measuring that crossover hides where conversions really originate.",
          impact: "Incorrect attribution and decisions based on incomplete data",
        },
      ],
    },
    methodology: {
      title: "How the strategy is approached",
      subtitle: "Audience research first, technical architecture second, content last",
      steps: [
        {
          number: "01",
          title: "English vs. Spanish search behavior",
          description:
            "Analysis of which topics in your category are predominantly searched in English, which in Spanish, which in both, and at what stage of the user journey each happens.",
        },
        {
          number: "02",
          title: "Real keyword localization",
          description:
            "Research into how Hispanic users in the US actually phrase their searches — not a translation of the English list — including borrowed English terms where the category genuinely uses them.",
        },
        {
          number: "03",
          title: "Hispanic market research by region",
          description:
            "Identifying meaningful variation by geography and predominant country of origin, without relying on generalizations or unverified statistics.",
        },
        {
          number: "04",
          title: "Bilingual site architecture",
          description:
            "Defining EN/ES structure (subfolders or subdomains), correctly implementing hreflang and canonicals, and checking that neither version ends up as thin or duplicate content.",
        },
        {
          number: "05",
          title: "Differentiated content strategy",
          description:
            "Spanish content built for its own search intent, not adapted from English as a second priority, with the same editorial depth in both languages.",
        },
        {
          number: "06",
          title: "Local search and buying behavior",
          description:
            "Optimizing local presence (Google Business Profile, directories, proximity signals) around how Hispanic users search for businesses in their preferred language.",
        },
        {
          number: "07",
          title: "Measuring the bilingual crossover",
          description:
            "Setting up analytics that can show when a user researches in one language and converts in another, so the real contribution of the Spanish version isn't underestimated.",
        },
      ],
      ctaText: "Request a Strategic SEO Review",
      ctaLink: "/en/free-seo-diagnosis",
    },
    scope: {
      includes: {
        title: "What this service includes",
        items: [
          "Bilingual (English/Spanish) search behavior research by category",
          "Keyword localization, not literal translation",
          "EN/ES architecture audit and fixes (hreflang, canonicals, URL structure)",
          "Content strategy differentiated by language and intent",
          "Local search optimization for Hispanic audiences",
          "Bilingual ecommerce considerations where applicable",
          "Measuring the user journey across both languages",
        ],
      },
      notIncludes: {
        title: "What it doesn't include",
        items: [
          "Expansion into other countries or international markets (see international SEO)",
          "Standalone translation services without a search strategy",
          "Paid ad campaign management (Google Ads, Meta Ads)",
          "Video production or graphic design",
          "Certified or legal document translation",
        ],
      },
    },
    forWho: {
      idealFor: {
        title: "Who this makes sense for",
        items: [
          "US businesses that already serve, or want to serve, Hispanic customers",
          "Brands with an English site considering a Spanish-language version",
          "Businesses with an existing Spanish presence that suspect it isn't performing",
          "Companies in categories where the buying decision often happens in the user's preferred language (health, financial services, legal, home services)",
          "Local or regional businesses with a significant Hispanic customer base",
        ],
      },
      notFor: {
        title: "Who should look elsewhere first",
        items: [
          "Companies looking to expand into Mexico, Spain, or Latin America as new markets (that's international SEO)",
          "Businesses without capacity to maintain content and customer support in both languages",
          "Brands looking for a quick translation without commitment to a sustained bilingual strategy",
        ],
      },
    },
    faq: {
      title: "Frequently asked questions",
      items: [
        {
          question: "Is translating my website to Spanish enough?",
          answer:
            "No. Translation solves the language, not the search intent or the technical architecture. Without localized keywords and correctly configured hreflang and canonicals, the Spanish version can end up competing poorly against — or even hurting — the English version.",
        },
        {
          question: "Does the Hispanic market search everything in Spanish?",
          answer:
            "Not necessarily. Many bilingual users switch between English and Spanish depending on the topic, device, or context. That's why search behavior research is the first step, not an assumption.",
        },
        {
          question: "Is this the same as international SEO?",
          answer:
            "No. Hispanic market SEO focuses on users living in the United States who search in English and/or Spanish. International SEO focuses on expanding into other countries like Mexico, Spain, or the rest of Latin America. They're different strategies, though they can share some language research.",
        },
        {
          question: "Do I need identical content in both languages?",
          answer:
            "Not always, and forcing it is often a mistake. Each language may need its own content angle based on how that audience segment searches, even when the underlying topic is the same.",
        },
        {
          question: "How do I know if my Spanish version is already working?",
          answer:
            "It's reviewed through Search Console segmented by language/country, analytics behavior, and an audit of how that version is indexed and linked compared to the English version.",
        },
      ],
    },
    related: {
      title: "Related services",
      subtitle: "To complement the bilingual strategy",
      links: [
        {
          label: "International SEO",
          description: "If the goal is expanding into other countries, not just reaching the US Hispanic market.",
          to: "/en/services/international-seo",
        },
        {
          label: "Local SEO",
          description: "Local search visibility for businesses with a Hispanic customer base nearby.",
          to: "/en/local-seo",
        },
        {
          label: "Ecommerce SEO",
          description: "Online store architecture and content built for bilingual shoppers.",
          to: "/en/services/ecommerce-seo",
        },
        {
          label: "Technical SEO",
          description: "The technical foundation an EN/ES architecture needs to actually work.",
          to: "/en/services/technical-seo",
        },
        {
          label: "SEO consulting",
          description: "Ongoing strategic guidance beyond a one-off project.",
          to: "/en/seo-consultant",
        },
      ],
    },
    llmSummary:
      "SEO service focused on reaching the US Hispanic market by accounting for bilingual (English/Spanish) search behavior, real keyword localization, and a correctly implemented EN/ES site architecture.",
    keyPoints: [
      "The same bilingual user may search in English or Spanish depending on context",
      "Keyword localization is not literal translation",
      "EN/ES architecture requires correct hreflang and canonicals",
      "There's meaningful variation by region and country of origin within the Hispanic market",
      "Distinct from international SEO, which focuses on expansion into other countries",
    ],
  },
};
